import type { RoundAggregate } from "@engine/round/aggregate";
import type { Track } from "@lib/types";
import { generalNotesCardState } from "@lib/generalNotesVisibility";

/**
 * Which of a recommender's steps the rail may ask for.
 *
 * A step exists only when its destination has something to show. Getting that
 * wrong is the rail's most-repeated bug — three review findings so far, each a
 * different field, each the same mistake of answering the question here instead
 * of asking the surface that renders it:
 *  - notes, tested with `trim()` on the raw string: blank rich text
 *    (`<p><br></p>`) is not blank to `trim`, and an anonymized aggregate stamps
 *    a note the viewer may not see with a NON-EMPTY sentinel — so the step both
 *    could not be completed and disclosed that a hidden note exists;
 *  - the profile summary, tested as "an artifact row exists" while the section
 *    renders only the newest one and only if it has content;
 *  - the overall score, first read from per-proposal star ratings, which is a
 *    different control entirely.
 *
 * So this is a pure function with its own viewer-matrix coverage rather than an
 * expression inside the hook: the leak case in particular is unreachable from
 * the e2e rig, which can only log in as a see-everything funder.
 */
export function recommenderStepAvailability({
  round,
  viewerTrack,
  viewerUserId,
  memberId,
  recommenderId,
  funderRecommenderWeights,
  membersWithProfileSummary,
}: {
  /** The viewer-scoped (already anonymized) aggregate. */
  round: RoundAggregate;
  /** The state context's viewer track — never a `round.members` lookup. */
  viewerTrack: Track;
  viewerUserId: string | undefined;
  /** The recommender's round-member id, or null when they have none. */
  memberId: string | null;
  recommenderId: string;
  /** The selected funder's per-recommender overall scores. */
  funderRecommenderWeights: Record<string, number> | undefined;
  /** Members whose latest profile summary actually renders (server-computed). */
  membersWithProfileSummary: ReadonlySet<string>;
}): {
  hasGeneralNotes: boolean;
  hasProfileSummary: boolean;
  hasScore: boolean;
} {
  return {
    // `generalNotesCardState`'s question, answered by the same helper the
    // General Round Notes card renders from. "notes" is the only state with
    // something to read: "placeholder" is an empty card and "hidden" must stay
    // indistinguishable from a recommender who never wrote one.
    hasGeneralNotes:
      memberId != null &&
      generalNotesCardState(round, viewerTrack, viewerUserId, memberId) ===
        "notes",
    hasProfileSummary:
      memberId != null && membersWithProfileSummary.has(memberId),
    // The recommender's OVERALL SCORE — the funder's own weight for that
    // person, the control on their page — not the per-proposal star ratings,
    // which feed the separate Scoring step. A missing weight means the app is
    // showing its own estimate, so only an explicit value counts; this is the
    // same expression the workspace calls `weightIsManual`.
    hasScore: funderRecommenderWeights?.[recommenderId] != null,
  };
}
